import { datadogRum } from '@datadog/browser-rum';
import i18next from 'i18next';

import { Auth, fetchAuth } from '@/hooks/useAuth';

import { handleApiError } from '@/utils/api-error-handler';
import { NativeAuth, signInWithRedirect } from '@/utils/auth';
import {
  permissionsWidgetInitializedAtom as permsWidgetInitializedAtom,
  setPermissionsFromToken,
} from '@/utils/permissions-widget';

import Constants from '@/constants';

import { store as jotaiStore } from '@/atoms';
import { userPrefsAtom } from '@/atoms/userPrefs';
import { parseAccessTokenClaims } from '@/okta/okta-utils';
import oktaAuth from '@/okta/oktaConfig';

export const initializeWebUser = async (): Promise<void> => {
  const authState = oktaAuth.authStateManager.getAuthState();

  const token = oktaAuth.getAccessToken();

  if (!token) {
    await signInWithRedirect();
    throw new Error('No access token');
  }

  if (Constants.ENV !== 'prod' && !jotaiStore.get(permsWidgetInitializedAtom)) {
    setPermissionsFromToken(
      parseAccessTokenClaims(authState?.accessToken?.claims),
    );
  }

  const auth = await getAuth();
  if (!auth) {
    throw new Error('No auth');
  }

  i18next.changeLanguage(auth.user?.language ?? 'en');
  jotaiStore.set(userPrefsAtom, {
    country: { id: auth.user?.country },
    language: auth.user?.language ?? '',
  });

  const returnTo = localStorage.getItem('returnTo');

  if (returnTo) {
    localStorage.removeItem('returnTo');
    history.replaceState({}, '', returnTo);
    window.dispatchEvent(new PopStateEvent('popstate'));
  } else if (window.location.pathname === '/login/callback') {
    history.replaceState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
};

export const initializeNativeUser = async ({ accessToken }: NativeAuth) => {
  if (!accessToken) {
    console.error('No access token or native user');
    return;
  }

  const auth = await getAuth();
  if (!auth) {
    return;
  }

  i18next.changeLanguage(
    window.native.isNative ? window.language : (auth.user?.language ?? 'en'),
  );
  jotaiStore.set(userPrefsAtom, {
    country: { id: auth.user?.country },
    language: auth.user?.language ?? '',
  });
};

const getAuth = async (): Promise<Auth | undefined> => {
  try {
    return await fetchAuth();
  } catch (err) {
    console.warn(err);
    datadogRum.addError(
      new Error(`Errror fetching auth token for user`, { cause: err }),
    );

    // Delegate to centralized error handler
    // Handles: skippable errors, Okta auth errors, 403 during impersonation,
    // 401 token errors (toast during impersonation, logout otherwise)
    await handleApiError(err, 'getAuth');

    throw err;
  }
};
